import {
  AppBar,
  Toolbar,
  Button,
  Stack,
  Typography,
  Badge,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useContext, useState } from "react";
import { UserContext } from "../context/UserContext";
import { Link, useNavigate } from "react-router-dom";
import DensityMediumIcon from "@mui/icons-material/DensityMedium";
import Drawer from "./Drawer";
import { useLocation } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert"; // Import confirmation alert
import "react-confirm-alert/src/react-confirm-alert.css"; // Import confirmation alert styles
import { scroller } from "react-scroll"; // Import scrolling functionality

// Navbar component for navigation bar
const Navbar = () => {
  const location = useLocation(); // Get current route location
  const [openDrawer, setOpenDrawer] = useState(false); // State to control mobile drawer menu
  const { userState, dispatch } = useContext(UserContext); // Get user state from context
  const navigate = useNavigate(); // React Router navigation hook

  // Function to handle "Shop Now" button click
  const handleShopNowClick = () => {
    if (location.pathname === "/") {
      // If already on home page, scroll to "products" section
      scroller.scrollTo("products", { smooth: true, duration: 500 });
    } else {
      // Navigate to home first, then scroll
      navigate("/");
      setTimeout(() => {
        scroller.scrollTo("products", { smooth: true, duration: 500 });
      }, 500);
    }
  };

  // Function to handle user logout
  const handleLogout = () => {
    confirmAlert({
      title: "Log out confirmation",
      message: "Do you really want to log out?",
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            const newCurrentUser = {
              username: "",
              fname: "",
              lname: "",
              email: "",
              password: "",
              cart: {
                products: [],
                amount: 0,
                total: 0,
              },
              purchasedProducts: [],
            };
            localStorage.setItem("currentUser", JSON.stringify(newCurrentUser)); // Clear user data from local storage
            dispatch({
              type: "logout",
            });
            navigate("/login"); // Redirect to login page
          },
        },
        {
          label: "No",
        },
      ],
    });
  };

  return (
    <AppBar>
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          backgroundColor: "#23262D",
        }}
      >
        {/* Store Logo */}
        <Stack>
          <Link to="/" style={{ textDecoration: "none" }}>
            <Typography
              variant="h6"
              color="#C9A227"
              sx={{
                textDecoration: "none",
                "&:hover": { color: "#B09020" },
              }}
            >
              Hasan Store
            </Typography>
          </Link>
        </Stack>

        {/* Navigation Buttons (Visible on larger screens) */}
        <Stack
          sx={{
            display: { xs: "none", sm: "flex" },
            flexDirection: "row",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {/* Home Button */}
          <Link to="/">
            <Button
              color="primary"
              sx={{
                backgroundColor:
                  location.pathname === "/" ? "#3A3F4B" : "transparent",
              }}
            >
              Home
            </Button>
          </Link>

          {/* Shop Now Button */}
          <Button color="primary" onClick={handleShopNowClick}>
            Shop Now
          </Button>

          {/* Cart Button (Visible only if user is logged in) */}
          {userState.username && (
            <Link to="/cart">
              <Button
                color="primary"
                sx={{
                  position: "relative",
                  backgroundColor:
                    location.pathname === "/cart" ? "#3A3F4B" : "transparent",
                }}
              >
                <Badge
                  badgeContent={userState.cart.amount} // Show cart item count
                  sx={{
                    "& .MuiBadge-badge": {
                      backgroundColor: "red",
                      color: "white",
                      top: -15,
                      right: -65,
                    },
                  }}
                />
                My Cart
              </Button>
            </Link>
          )}

          {/* Profile Button (Visible only if user is logged in) */}
          {userState.username && (
            <Link
              to="/profile"
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                padding: "5px 10px",
                borderRadius: "5px",
                backgroundColor:
                  location.pathname === "/profile" ? "#3A3F4B" : "transparent",
              }}
            >
              <AccountCircleIcon
                color="primary"
                fontSize="medium"
                sx={{
                  cursor: "pointer",
                }}
              />
            </Link>
          )}

          {/* Log In & Sign Up Buttons (Visible only if user is not logged in) */}
          {!userState.username && (
            <Link to="/login">
              <Button
                color="primary"
                sx={{
                  backgroundColor:
                    location.pathname === "/login" ? "#3A3F4B" : "transparent",
                }}
              >
                Log In
              </Button>
            </Link>
          )}
          {!userState.username && (
            <Link to="/signup">
              <Button
                color="primary"
                sx={{
                  backgroundColor:
                    location.pathname === "/signup" ? "#3A3F4B" : "transparent",
                }}
              >
                Sign Up
              </Button>
            </Link>
          )}

          {/* Log Out Button (Visible only if user is logged in) */}
          {userState.username && (
            <Button color="primary" onClick={handleLogout}>
              Log Out
            </Button>
          )}
        </Stack>

        {/* Drawer Menu Icon (Visible on smaller screens) */}
        <DensityMediumIcon
          color="primary"
          sx={{ display: { xs: "block", sm: "none", cursor: "pointer" } }}
          onClick={() => setOpenDrawer(true)}
        />

        {/* Mobile Navigation Drawer */}
        <Drawer openDrawer={openDrawer} setOpenDrawer={setOpenDrawer} />
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
